import { ReactElement, ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  RenderProps,
} from '@ff-it/ui';
import { Form, FormError, SubmissionErrors, Submit, SubmitHandler } from '@ff-it/form';

type TitleOrHeader =
  | {
      title?: ReactNode;
      header?: never;
    }
  | {
      title?: never;
      header?: ReactNode;
    };

type DialogFormProps = {
  children: ReactNode;
  decorators?: any;
  initialValues?: any;
  onRemove?: () => Promise<void>;
  className?: string;
  disabled?: boolean;
} & TitleOrHeader;

export function DialogForm<R = void>({
  onDismiss,
  onSubmit,
  title,
  header,
  children,
  decorators,
  initialValues,
  submitHandler,
  onRemove,
  className,
  disabled,
}: DialogFormProps &
  RenderProps<R> & {
    // submitHanlder and resolves to void. Or resolves to form values
    submitHandler?: R extends void ? SubmitHandler<any> : never;
  }): ReactElement {
  return (
    <Form
      noValidate
      autoComplete="off"
      onSubmit={async (values, form) => {
        if (submitHandler) {
          const err = await Promise.resolve(submitHandler(values, form));

          if (err) {
            return err;
          }
          onSubmit(undefined);
        } else {
          onSubmit(values as R);
        }
      }}
      initialValues={initialValues}
      renderErrors={false}
      decorators={decorators}
      className={className}
      disabled={disabled}
      display="contents"
    >
      {header}
      {title && <DialogHeader title={title} />}
      <DialogBody>
        <FormError />
        {children}
      </DialogBody>
      <DialogFooter>
        <DialogClose />
        {onRemove && (
          <Button type="button" variant="danger" ml="1" onClick={() => onRemove().then(onDismiss)}>
            Delete
          </Button>
        )}
        {!disabled && <Submit ml="auto">Submit</Submit>}
      </DialogFooter>
    </Form>
  );
}

type RenderDialogFormProps = {
  onClose: () => void;
  testId?: string;
  submitHandler: (values: any) => Promise<SubmissionErrors | void>;
};

export function RenderDialogForm({ onClose, testId, ...rest }: RenderDialogFormProps & DialogFormProps): ReactElement {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent testId={testId}>
        <DialogForm {...rest} onDismiss={onClose} onSubmit={onClose} />
      </DialogContent>
    </Dialog>
  );
}
